// =========================================================================
// Nagi #01 「閉じる川」 — Web MVP entry point + routing (W7)
// =========================================================================
// spec/closing_river_web_mvp_v2.md §「画面フロー」
//
// 役割:
//   - catalog/main.json / archive.json / tutorial.json を読み込み、version gate に通す
//   - Title / Tutorial / Board List / Game の top-level state machine を回す
//   - 各画面の mount/unmount を管理
//   - tutorial 完走 → progress.markTutorialSeen → title 復帰
//   - game 完了 → progress.markComplete → title 復帰
//
// 触ってはいけない: canvas.ts, preview.ts, confirm-modal.ts, turn-indicator.ts,
// edge-interaction.ts, game/controller.ts, game/end-overlay.ts, tutorial/*
// (本 module は上記を import するが、編集しない)
// =========================================================================

import { env } from './env.js';
import {
  loadCatalogManifest,
  evaluateVersionGate,
  type CatalogManifest,
  type PlayableBoard,
  type TutorialBoard,
  type BoardViewModel,
  type Badge,
  type VersionGateOutcome,
  type CatalogBoard,
} from './catalog/manifest.js';
import { createTitleScreen, setArchiveBoardIds } from './ui/title.js';
import {
  createBoardList,
  buildUnachievedMenu,
  type BoardListMode,
} from './ui/board-list.js';
import {
  markComplete,
  markTutorialSeen,
  pickUnplayedRandom,
  type BestBadge,
} from './progress.js';

// W3 / W4 / W5 / W6 が公開する実 API (stub は使わない)
import { renderBoard } from './game/canvas.js';
import { createEdgeInteraction } from './game/edge-interaction.js';
import { createConfirmModal } from './game/confirm-modal.js';
import { createTurnIndicator } from './game/turn-indicator.js';
import { createEndOverlay } from './game/end-overlay.js';
import { createGameController } from './game/controller.js';
import { createTutorialController } from './tutorial/index.js';

// -------------------------------------------------------------------------
// Catalog modules (import.meta.glob は top-level 同期評価)
// -------------------------------------------------------------------------
//
// **重要**: この `const` は bootstrap(app) より前に置くこと。
// 直下で `bootstrap(app)` を即時呼ぶと、async 関数本体は最初の `await` に
// 到達するまで同期的に走るため、`loadManifest` 内で CATALOG_MODULES を
// 参照した瞬間に TDZ (temporal dead zone) ReferenceError を起こす。
// (実際 2026-05-13 Playwright 起動時に「Cannot access 'CATALOG_MODULES'
// before initialization」 が出た。これがその修正)
//
// Vite の `import.meta.glob({ eager: true })` は build 時に静的展開され、
// 該当 JSON がまだ未生成でも build/typecheck は通る (該当 key が無いだけ)。
// runtime で key が無いときは tutorial.json fingerprint を借りた空 manifest
// にフォールバック (= main.json / archive.json 未生成でも起動できる)。

const CATALOG_MODULES = import.meta.glob<unknown>('./catalog/*.json', {
  eager: true,
  import: 'default',
});

// -------------------------------------------------------------------------
// 起動
// -------------------------------------------------------------------------

const app = document.getElementById('app');
if (!app) {
  throw new Error('main.ts: #app element not found in index.html');
}

bootstrap(app).catch((err) => {
  // eslint-disable-next-line no-console
  console.error('[nagi/main] bootstrap failed:', err);
  renderFatalScreen(
    app,
    '起動に失敗しました',
    err instanceof Error ? err.message : String(err),
  );
});

// -------------------------------------------------------------------------
// View 状態 (top-level state machine)
// -------------------------------------------------------------------------

type View =
  | { readonly name: 'title' }
  | { readonly name: 'tutorial' }
  | { readonly name: 'main-list' }
  | { readonly name: 'archive-list' }
  | { readonly name: 'unachieved-menu' }
  | { readonly name: 'unachieved-silent-list' }
  | { readonly name: 'unachieved-clean-list' }
  | { readonly name: 'all-list' }
  | { readonly name: 'game'; readonly boardId: string };

interface AppContext {
  readonly root: HTMLElement;
  readonly mainBoards: readonly PlayableBoard[];
  readonly archiveBoards: readonly PlayableBoard[];
  readonly tutorialBoards: readonly TutorialBoard[];
  readonly mainManifest: CatalogManifest;
  readonly archiveManifest: CatalogManifest;
}

// 各画面の active handle (destroy 用)
let activeDestroy: (() => void) | null = null;

// -------------------------------------------------------------------------
// bootstrap
// -------------------------------------------------------------------------

async function bootstrap(root: HTMLElement): Promise<void> {
  root.innerHTML = '';
  renderLoadingScreen(root, 'catalog を読み込み中…');

  let mainManifest: CatalogManifest;
  let archiveManifest: CatalogManifest;
  let tutorialManifest: CatalogManifest;
  try {
    [mainManifest, archiveManifest, tutorialManifest] = await Promise.all([
      loadManifest('main'),
      loadManifest('archive'),
      loadManifest('tutorial'),
    ]);
  } catch (err) {
    renderFatalScreen(
      root,
      'catalog のロードに失敗しました',
      err instanceof Error ? err.message : String(err),
    );
    return;
  }

  // Version gate: spec §「Version Gate」
  // env fingerprint を runtime で計算する手段は MVP には無い (engine ソースを web に
  // 配らない方針) ため、catalog 自体の fingerprint で互換性検査を行う形になる。
  // catalog manifest 同士の rulesFingerprint 一致を最低限のセーフティとして見る。
  const gateOutcome = crossCatalogGate(mainManifest, archiveManifest);
  if (gateOutcome.kind !== 'ok') {
    renderFatalScreen(
      root,
      'catalog 互換性エラー',
      describeGateOutcome(gateOutcome),
    );
    return;
  }

  const mainBoards = filterPlayable(mainManifest.boards);
  const archiveBoards = filterPlayable(archiveManifest.boards);
  const tutorialBoards = filterTutorial(tutorialManifest.boards);

  const ctx: AppContext = {
    root,
    mainBoards,
    archiveBoards,
    tutorialBoards,
    mainManifest,
    archiveManifest,
  };

  route(ctx, { name: 'title' });
}

// -------------------------------------------------------------------------
// catalog loader
// -------------------------------------------------------------------------
//
// CATALOG_MODULES は本ファイル冒頭 (import 直後) に宣言済み。
// async loadManifest は本体最初の `await` 前に CATALOG_MODULES を読むため、
// TDZ 回避のため宣言位置が重要 (上に移動済)。

async function loadManifest(
  kind: 'main' | 'archive' | 'tutorial',
): Promise<CatalogManifest> {
  const fileName =
    kind === 'main' ? 'main.json' : kind === 'archive' ? 'archive.json' : 'tutorial.json';
  const key = `./catalog/${fileName}`;
  const raw = CATALOG_MODULES[key];
  if (raw === undefined) {
    if (kind === 'tutorial') {
      // tutorial は W2 が catalog/tutorial.json を必ず置いている前提なので fatal。
      throw new Error(`catalog/${fileName} が存在しません`);
    }
    // main / archive は W2 の build-catalog 未実行でも UI 側を動かせるよう、
    // 空 manifest を返す (rules/solver fingerprint は tutorial と揃えて gate 通過)。
    // tutorial.json から fingerprint を借りる方式。
    const tutorialRaw = CATALOG_MODULES['./catalog/tutorial.json'];
    if (tutorialRaw === undefined) {
      throw new Error(`catalog/${fileName} と catalog/tutorial.json の両方が無い`);
    }
    const tutorial = loadCatalogManifest(tutorialRaw);
    // eslint-disable-next-line no-console
    console.warn(
      `[nagi/main] ${fileName} が見つからないため、空 manifest にフォールバック (W2 build-catalog 未実行)`,
    );
    return {
      ...tutorial,
      sourceSeedRange: undefined,
      aggregate: undefined,
      boards: [],
    };
  }
  return loadCatalogManifest(raw);
}

// -------------------------------------------------------------------------
// Version gate (catalog 同士の最低限の互換性検査)
// -------------------------------------------------------------------------

function crossCatalogGate(
  main: CatalogManifest,
  archive: CatalogManifest,
): VersionGateOutcome {
  // engine 名 / schema を main 側基準で archive 側に問う形にする。
  // (両者は同じ build で生成される前提なので、ここで mismatch なら ship 不可)
  return evaluateVersionGate(archive, {
    rulesFingerprint: main.rulesFingerprint,
    solverFingerprint: main.solverFingerprint,
    generatorFingerprint: main.generatorFingerprint,
    enginePackageName: main.enginePackageName,
    engineVersion: main.engineVersion,
  });
}

function describeGateOutcome(o: VersionGateOutcome): string {
  switch (o.kind) {
    case 'ok':
      return 'ok';
    case 'schema_mismatch':
      return `schemaVersion mismatch (expected ${o.expected}, got ${o.got})`;
    case 'engine_name_mismatch':
      return `enginePackageName mismatch (expected ${o.expected}, got ${o.got})`;
    case 'rules_mismatch':
      return `rulesFingerprint mismatch:\n  catalog=${o.catalog}\n  env=${o.env}`;
    case 'solver_mismatch':
      return `solverFingerprint mismatch:\n  catalog=${o.catalog}\n  env=${o.env}`;
    case 'generator_mismatch':
      return `generatorFingerprint mismatch (warning):\n  catalog=${o.catalog}\n  env=${o.env}`;
  }
}

// -------------------------------------------------------------------------
// board 分類 (union → 種別ごとの array)
// -------------------------------------------------------------------------

function filterPlayable(boards: readonly CatalogBoard[]): readonly PlayableBoard[] {
  return boards.filter(
    (b): b is PlayableBoard => b.source === 'main' || b.source === 'archive',
  );
}

function filterTutorial(boards: readonly CatalogBoard[]): readonly TutorialBoard[] {
  return boards.filter((b): b is TutorialBoard => b.source === 'handmade-tutorial');
}

// -------------------------------------------------------------------------
// routing
// -------------------------------------------------------------------------

function route(ctx: AppContext, view: View): void {
  if (activeDestroy) {
    try {
      activeDestroy();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[nagi/main] previous view destroy threw:', err);
    }
    activeDestroy = null;
  }
  ctx.root.innerHTML = '';

  switch (view.name) {
    case 'title':
      mountTitle(ctx);
      break;
    case 'tutorial':
      mountTutorial(ctx);
      break;
    case 'main-list':
      mountBoardList(ctx, 'main');
      break;
    case 'archive-list':
      mountBoardList(ctx, 'archive');
      break;
    case 'all-list':
      mountBoardList(ctx, 'all');
      break;
    case 'unachieved-silent-list':
      mountBoardList(ctx, 'unachieved-silent');
      break;
    case 'unachieved-clean-list':
      mountBoardList(ctx, 'unachieved-clean');
      break;
    case 'unachieved-menu':
      mountUnachievedMenu(ctx);
      break;
    case 'game':
      mountGame(ctx, view.boardId);
      break;
  }
}

// -------------------------------------------------------------------------
// 各画面 mount 関数
// -------------------------------------------------------------------------

function mountTitle(ctx: AppContext): void {
  const handle = createTitleScreen();
  const archiveIds = ctx.archiveBoards.map((b) => b.id);
  handle.mount(ctx.root, {
    onTutorial: () => route(ctx, { name: 'tutorial' }),
    onMain: () => route(ctx, { name: 'main-list' }),
    onArchive: () => route(ctx, { name: 'archive-list' }),
    onRandom: () => handleRandom(ctx),
  });
  setArchiveBoardIds(handle, archiveIds);
  activeDestroy = () => handle.destroy();
}

function mountBoardList(ctx: AppContext, mode: BoardListMode): void {
  const handle = createBoardList();
  const source =
    mode === 'main'
      ? ctx.mainBoards
      : // archive / all / unachieved-* は archive を母集合とする
        ctx.archiveBoards;
  handle.mount(ctx.root, source, mode, {
    onPick: (boardId) => route(ctx, { name: 'game', boardId }),
    onBack: () => route(ctx, { name: 'title' }),
  });
  activeDestroy = () => handle.destroy();
}

function mountUnachievedMenu(ctx: AppContext): void {
  const node = buildUnachievedMenu(ctx.archiveBoards, {
    onSilent: () => route(ctx, { name: 'unachieved-silent-list' }),
    onClean: () => route(ctx, { name: 'unachieved-clean-list' }),
    onAll: () => route(ctx, { name: 'all-list' }),
    onBack: () => route(ctx, { name: 'title' }),
  });
  ctx.root.appendChild(node);
  activeDestroy = () => {
    if (node.parentNode) node.parentNode.removeChild(node);
  };
}

function mountTutorial(ctx: AppContext): void {
  if (ctx.tutorialBoards.length === 0) {
    // catalog にチュートリアル盤面が無い (build:tutorial 未実行) ケースの
    // フォールバック。即 title 復帰する代わりに小さな案内を出す。
    renderFatalScreen(
      ctx.root,
      'tutorial board が catalog にありません',
      'npm run build:tutorial を実行してから build/dev してください。',
    );
    return;
  }

  // tutorial controller は spec §「Tutorial 仕様」 4 step + handmade board を
  // promptContainer / canvasContainer / controlsContainer の 3 領域に描画する。
  // W7 はその layout (上部 prompt / 中央 canvas / 下部 controls) を組み立てる。
  const wrapper = document.createElement('div');
  wrapper.style.cssText = [
    'flex: 1',
    'display: flex',
    'flex-direction: column',
    'padding: max(env(safe-area-inset-top), 16px) 16px max(env(safe-area-inset-bottom), 16px)',
    'gap: 16px',
    'min-height: 100vh',
    'box-sizing: border-box',
  ].join(';');

  const promptContainer = document.createElement('div');
  promptContainer.style.cssText = ['flex: 0 0 auto'].join(';');

  const canvasContainer = document.createElement('div');
  canvasContainer.style.cssText = [
    'flex: 1 1 auto',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'min-height: 360px',
  ].join(';');

  const controlsContainer = document.createElement('div');
  controlsContainer.style.cssText = ['flex: 0 0 auto'].join(';');

  wrapper.appendChild(promptContainer);
  wrapper.appendChild(canvasContainer);
  wrapper.appendChild(controlsContainer);
  ctx.root.appendChild(wrapper);

  // W6 の RenderBoardFn (= 単に描画関数) と W3 の renderBoard (= factory 型) を
  // 繋ぐ薄い adapter。tutorial 内で board が切り替わる (tutorial-01 → tutorial-02)
  // たびに新しい CanvasHandle を作る方式。
  // (CanvasHandle.update でも可だが、tutorial 内蔵の simple renderer と
  //  動作セマンティクスを揃えるため毎回 full create が安全)
  let activeCanvasHandle: ReturnType<typeof renderBoard> | null = null;
  const renderBoardAdapter = (container: HTMLElement, vm: BoardViewModel): void => {
    if (activeCanvasHandle) {
      // 同 container への update なら handle.update で済むが、container が
      // 切り替わる可能性もあるので destroy → 新規 create に統一。
      activeCanvasHandle.destroy();
      activeCanvasHandle = null;
    }
    activeCanvasHandle = renderBoard(container, vm);
  };

  const tutorialHandle = createTutorialController({
    canvasContainer,
    promptContainer,
    controlsContainer,
    boards: ctx.tutorialBoards,
    renderBoard: renderBoardAdapter,
    onComplete: () => {
      markTutorialSeen();
      // spec §「Tutorial 仕様」 step 4: 「やってみる」 → main 9 問へ
      route(ctx, { name: 'main-list' });
    },
    onSkip: () => {
      // skip でも tutorialSeen を立てる (2 度目以降の Title に「ルールを見る」 が
      // 残っていてもユーザーが押し直せるので、再表示阻止より「進捗の正直さ」 を取る)
      markTutorialSeen();
      route(ctx, { name: 'title' });
    },
  });
  tutorialHandle.start();

  activeDestroy = () => {
    tutorialHandle.destroy();
    if (activeCanvasHandle) {
      activeCanvasHandle.destroy();
      activeCanvasHandle = null;
    }
    if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
  };
}

function mountGame(ctx: AppContext, boardId: string): void {
  // すべての PlayableBoard (main + archive) から検索
  const board =
    ctx.mainBoards.find((b) => b.id === boardId) ??
    ctx.archiveBoards.find((b) => b.id === boardId);
  if (!board) {
    renderFatalScreen(ctx.root, 'board not found', `boardId=${boardId}`);
    return;
  }

  // ---- game 画面 layout ----
  const wrapper = document.createElement('div');
  wrapper.style.cssText = [
    'flex: 1',
    'display: flex',
    'flex-direction: column',
    'padding: max(env(safe-area-inset-top), 8px) 8px 0',
    'gap: 12px',
    'min-height: 100vh',
    'box-sizing: border-box',
    'position: relative',
  ].join(';');

  // 上部: turn-indicator + 戻るボタン (W4 turn-indicator は自前で描画)
  const topBar = document.createElement('div');
  topBar.style.cssText = [
    'display: flex',
    'align-items: center',
    'gap: 12px',
    'padding: 4px 8px',
  ].join(';');

  const backBtn = document.createElement('button');
  backBtn.type = 'button';
  backBtn.textContent = '← タイトル';
  backBtn.style.cssText = [
    'padding: 6px 10px',
    'font-size: var(--font-sm)',
    'background: transparent',
    'border: 1px solid var(--border)',
    'border-radius: var(--radius-sm)',
    'color: var(--text-dim)',
  ].join(';');
  const turnIndicatorHost = document.createElement('div');
  turnIndicatorHost.style.cssText = ['flex: 1'].join(';');

  const boardLabel = document.createElement('span');
  boardLabel.textContent = `${board.id}`;
  boardLabel.style.cssText = [
    'font-size: 11px',
    'color: var(--text-dim)',
    'letter-spacing: 0.06em',
  ].join(';');

  topBar.appendChild(backBtn);
  topBar.appendChild(turnIndicatorHost);
  topBar.appendChild(boardLabel);
  wrapper.appendChild(topBar);

  // 中央: canvas
  const canvasContainer = document.createElement('div');
  canvasContainer.style.cssText = [
    'flex: 1 1 auto',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'min-height: 360px',
    'padding-bottom: 96px', // preview-bar (W4 fixed bottom) ぶん空ける
  ].join(';');
  wrapper.appendChild(canvasContainer);

  // preview-bar は W4 仕様で fixed 配置だが、container を明示で渡せるので
  // wrapper 内に配置する (overlay 系は document.body)。
  const previewBarContainer = document.createElement('div');
  previewBarContainer.style.cssText = [
    'position: fixed',
    'left: 0',
    'right: 0',
    'bottom: 0',
    'z-index: 5',
  ].join(';');
  wrapper.appendChild(previewBarContainer);

  ctx.root.appendChild(wrapper);

  // ---- 初期 ViewModel (controller.start 内で即上書きされる) ----
  const initialVm: BoardViewModel = {
    boardId: board.id,
    dimension: '1D',
    nodes: [],
    activeEdges: [],
    historyEdges: [],
    movesUsed: 0,
    turnLimit: board.N,
    seedLabel: board.seed,
  };

  // ---- handles ----
  const canvas = renderBoard(canvasContainer, initialVm);
  const edgeInteraction = createEdgeInteraction();
  const confirmModal = createConfirmModal(document.body);
  const turnIndicator = createTurnIndicator(turnIndicatorHost);
  const endOverlay = createEndOverlay(document.body);

  const controller = createGameController({
    canvas,
    edgeInteraction,
    confirmModal,
    turnIndicator,
    endOverlay,
    previewBarContainer,
    board,
    onComplete: (badge: Badge) => {
      // Badge → BestBadge (同 shape)。bestMoves は silent 達成時のみ存在。
      const best: BestBadge = {
        cleared: badge.cleared,
        clean: badge.clean,
        silent: badge.silent,
        ...(typeof badge.bestMoves === 'number'
          ? { bestMoves: badge.bestMoves }
          : {}),
      };
      // markComplete 自体が「ベスト OR 累積」 でマージするので無条件に呼ぶ。
      // ただし cleared=clean=silent=false の純粋敗北は呼ばない (controller 側で
      // 勝利時のみ onComplete を呼ぶ契約だが、二重保険として check)。
      if (best.cleared || best.clean || best.silent) {
        markComplete(board.id, best);
      }
    },
    onExit: (reason: 'home' | 'pickAnother') => {
      // controller / handles を全部破棄してから遷移 (activeDestroy で実行)
      if (reason === 'pickAnother') {
        route(ctx, {
          name: board.source === 'main' ? 'main-list' : 'archive-list',
        });
      } else {
        route(ctx, { name: 'title' });
      }
    },
  });

  backBtn.addEventListener('click', () => {
    // ユーザーが「← タイトル」 をタップ。controller を経由せず onExit と同じ
    // 振る舞いをする (アニメ中の状態を考えると controller.destroy が安全)。
    route(ctx, { name: 'title' });
  });

  controller.start();

  activeDestroy = () => {
    try {
      controller.destroy();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[nagi/main] controller.destroy threw:', err);
    }
    // W5 controller.destroy が canvas / edgeInteraction / endOverlay 等を
    // 全部 destroy する想定だが、念のため個別にも呼んでおく (二重 destroy は
    // 各 handle 内で no-op になる契約)。
    try { canvas.destroy(); } catch { /* noop */ }
    try { confirmModal.destroy(); } catch { /* noop */ }
    try { turnIndicator.destroy(); } catch { /* noop */ }
    try { endOverlay.destroy(); } catch { /* noop */ }
    // edgeInteraction は destroy が無い (detach のみ)。controller.destroy が
    // detach するため明示呼び出しは省略。
    if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
  };
}

// -------------------------------------------------------------------------
// ランダムボタン: 枯渇判定
// -------------------------------------------------------------------------

function handleRandom(ctx: AppContext): void {
  const archiveIds = ctx.archiveBoards.map((b) => b.id);
  const pickedId = pickUnplayedRandom(archiveIds);
  if (pickedId !== null) {
    route(ctx, { name: 'game', boardId: pickedId });
    return;
  }
  // 全完了 → 「未達成から選ぶ」 メニュー
  route(ctx, { name: 'unachieved-menu' });
}

// -------------------------------------------------------------------------
// 共通: loading / fatal の最小 UI
// -------------------------------------------------------------------------

function renderLoadingScreen(container: HTMLElement, message: string): void {
  container.innerHTML = '';
  const div = document.createElement('div');
  div.style.cssText = [
    'flex: 1',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'color: var(--text-dim)',
    'font-size: var(--font-sm)',
    'min-height: 100vh',
  ].join(';');
  div.textContent = message;
  container.appendChild(div);
}

function renderFatalScreen(container: HTMLElement, title: string, detail: string): void {
  container.innerHTML = '';
  const wrapper = document.createElement('div');
  wrapper.style.cssText = [
    'flex: 1',
    'display: flex',
    'flex-direction: column',
    'gap: 12px',
    'padding: 32px 20px',
    'color: var(--text)',
    'min-height: 100vh',
    'box-sizing: border-box',
  ].join(';');

  const h = document.createElement('h1');
  h.textContent = title;
  h.style.cssText = [
    'margin: 0',
    'font-size: var(--font-lg)',
    'color: var(--danger-soft)',
  ].join(';');

  const p = document.createElement('pre');
  p.textContent = detail;
  p.style.cssText = [
    'margin: 0',
    'font-family: ui-monospace, SFMono-Regular, Menlo, monospace',
    'font-size: 12px',
    'color: var(--text-dim)',
    'white-space: pre-wrap',
    'background: var(--bg-elev)',
    'padding: 12px',
    'border-radius: var(--radius-md)',
    'border: 1px solid var(--border)',
  ].join(';');

  const meta = document.createElement('p');
  meta.textContent = `engine=${env.engineName}@${env.engineVersion}`;
  meta.style.cssText = ['font-size: 11px', 'color: var(--text-dim)', 'margin: 0'].join(
    ';',
  );

  wrapper.appendChild(h);
  wrapper.appendChild(p);
  wrapper.appendChild(meta);
  container.appendChild(wrapper);
}
